import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";

const {
  FiActivity,
  FiAlertCircle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiCloud,
  FiCpu,
  FiDatabase,
  FiGlobe,
  FiHeart,
  FiInfo,
  FiLink,
  FiRadio,
  FiRefreshCw,
  FiServer,
  FiShield,
  FiSmartphone,
  FiWatch,
  FiWifi,
  FiZap,
} = FiIcons;

// ─── Provider data ────────────────────────────────────────────────────────────
const PROVIDERS = [
  {
    id: "apple",
    name: "Apple Watch",
    manufacturer: "Apple Inc.",
    icon: FiWatch,
    status: "active",
    webhookRoute: "POST /api/v1/integrations/webhook/apple",
    normaliser: "mapProviderToSchema()",
    description: "HealthKit vitals via device webhook. Heart rate, SpO₂, activity.",
    supportedVitals: ["Heart Rate", "SpO₂", "Steps", "HRV", "Respiratory Rate"],
    color: "gray",
    accent: "#1d1d1f",
    badge: "bg-green-100 text-green-800",
    note: null,
  },
  {
    id: "garmin",
    name: "Garmin Connect",
    manufacturer: "Garmin Ltd.",
    icon: FiActivity,
    status: "active",
    webhookRoute: "POST /api/v1/integrations/webhook/garmin",
    normaliser: "mapProviderToSchema()",
    description: "Connect IQ webhook bridge. Vitals, GPS, stress score.",
    supportedVitals: ["Heart Rate", "SpO₂", "Steps", "GPS", "Stress Score"],
    color: "blue",
    accent: "#007CC3",
    badge: "bg-green-100 text-green-800",
    note: null,
  },
  {
    id: "generic_api",
    name: "Generic API",
    manufacturer: "Device-Agnostic",
    icon: FiGlobe,
    status: "active",
    webhookRoute: "POST /api/v1/integrations/webhook/:provider",
    normaliser: "mapProviderToSchema() — fallback path",
    description: "Accepts any conforming payload via the generic fallback normaliser.",
    supportedVitals: ["Any — mapped via fallback schema"],
    color: "indigo",
    accent: "#4F46E5",
    badge: "bg-green-100 text-green-800",
    note: null,
  },
  {
    id: "fitbit",
    name: "Fitbit",
    manufacturer: "Google LLC",
    icon: FiHeart,
    status: "pending",
    webhookRoute: "—",
    normaliser: "—",
    description: "Fitbit Web API integration. Pending backend + database support confirmation.",
    supportedVitals: ["Heart Rate", "SpO₂", "Sleep", "Steps", "Temperature"],
    color: "teal",
    accent: "#00B0B9",
    badge: "bg-yellow-100 text-yellow-800",
    note: "Backend and devices table support must be confirmed before enabling.",
  },
  {
    id: "oura",
    name: "Oura Ring",
    manufacturer: "Oura Health Oy",
    icon: FiRadio,
    status: "pending",
    webhookRoute: "—",
    normaliser: "—",
    description: "Oura Cloud API. Readiness, sleep, and recovery metrics pending integration.",
    supportedVitals: ["Readiness Score", "Sleep Stages", "HRV", "Temperature"],
    color: "purple",
    accent: "#7C3AED",
    badge: "bg-yellow-100 text-yellow-800",
    note: "Backend and devices table support must be confirmed before enabling.",
  },
  {
    id: "bluetooth",
    name: "Bluetooth / BLE",
    manufacturer: "Generic BLE Devices",
    icon: FiWifi,
    status: "pending",
    webhookRoute: "—",
    normaliser: "—",
    description: "Direct BLE device bridge for bedside monitors and non-internet devices.",
    supportedVitals: ["Varies by device"],
    color: "sky",
    accent: "#0284C7",
    badge: "bg-yellow-100 text-yellow-800",
    note: "Requires a local BLE-to-HTTP bridge. Not yet scoped.",
  },
];

// ─── Gateway pipeline steps ───────────────────────────────────────────────────
const PIPELINE_STEPS = [
  { label: "Device / Provider", icon: FiSmartphone, detail: "Apple Watch · Garmin · Generic API" },
  { label: "Webhook Endpoint",  icon: FiLink,       detail: "POST /api/v1/integrations/webhook/:provider" },
  { label: "Wearable Bridge",   icon: FiServer,     detail: "wearableBridge.js · mapProviderToSchema()" },
  { label: "Ingestion Service", icon: FiZap,        detail: "ingestionService.js · /vitals" },
  { label: "Real-time Broadcast", icon: FiRadio,    detail: "vitals_update · patient_vitals_sync · vitals_sync" },
  { label: "Supabase DB",       icon: FiDatabase,   detail: "telemetry_events · devices (pending approval)" },
];

// ─── Status pill ──────────────────────────────────────────────────────────────
const StatusPill = ({ status }) => {
  const map = {
    active:  { label: "Active",  cls: "bg-green-100 text-green-800",  dot: "bg-green-500" },
    pending: { label: "Pending", cls: "bg-yellow-100 text-yellow-800", dot: "bg-yellow-500" },
    error:   { label: "Error",   cls: "bg-red-100 text-red-800",      dot: "bg-red-500" },
  };
  const s = map[status] ?? map.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

// ─── Provider card ────────────────────────────────────────────────────────────
const ProviderCard = ({ provider }) => {
  const [expanded, setExpanded] = useState(false);
  const isPending = provider.status === "pending";

  return (
    <motion.div
      layout
      className={`bg-white rounded-xl border transition-all duration-200 overflow-hidden ${
        isPending
          ? "border-gray-200 opacity-80"
          : "border-gray-200 hover:border-blue-300 hover:shadow-md"
      }`}
    >
      {/* Card header */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          {/* Icon + name */}
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isPending ? "bg-gray-100" : "bg-blue-50"
              }`}
            >
              <SafeIcon
                icon={provider.icon}
                className={`text-lg ${isPending ? "text-gray-400" : "text-blue-600"}`}
              />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 text-sm leading-tight">
                {provider.name}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {provider.manufacturer}
              </div>
            </div>
          </div>
          <StatusPill status={provider.status} />
        </div>

        <p className="mt-3 text-xs text-gray-600 leading-relaxed">
          {provider.description}
        </p>

        {/* Pending notice */}
        {provider.note && (
          <div className="mt-3 flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-2.5">
            <SafeIcon icon={FiInfo} className="text-yellow-600 text-xs flex-shrink-0 mt-0.5" />
            <p className="text-xs text-yellow-800">{provider.note}</p>
          </div>
        )}

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors font-medium"
        >
          {expanded ? "Hide details" : "View details"}
          <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="text-xs" />
        </button>
      </div>

      {/* Expanded detail */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="detail"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-100 bg-gray-50 px-5 py-4 space-y-3">
              {!isPending && (
                <>
                  <div>
                    <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      Webhook Route
                    </div>
                    <code className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded font-mono block">
                      {provider.webhookRoute}
                    </code>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
                      Normaliser
                    </div>
                    <code className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded font-mono block">
                      {provider.normaliser}
                    </code>
                  </div>
                </>
              )}
              <div>
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                  Supported Vitals
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {provider.supportedVitals.map((v) => (
                    <span
                      key={v}
                      className="text-[11px] bg-white border border-gray-200 text-gray-700 px-2 py-0.5 rounded-full"
                    >
                      {v}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Pipeline diagram ─────────────────────────────────────────────────────────
const GatewayPipeline = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-6">
    <div className="flex items-center gap-2 mb-5">
      <SafeIcon icon={FiServer} className="text-blue-600" />
      <h3 className="font-semibold text-gray-900">Telemetry Gateway Pipeline</h3>
      <span className="ml-auto text-[11px] bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-medium">
        Backend built · UI exposure in progress
      </span>
    </div>

    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-0">
      {PIPELINE_STEPS.map((step, i) => (
        <React.Fragment key={step.label}>
          {/* Step node */}
          <div className="flex flex-col items-center text-center flex-1 min-w-0 px-1">
            <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center mb-2 flex-shrink-0">
              <SafeIcon icon={step.icon} className="text-blue-600 text-sm" />
            </div>
            <div className="text-[11px] font-semibold text-gray-800 leading-tight">
              {step.label}
            </div>
            <div className="text-[10px] text-gray-500 mt-0.5 leading-snug hidden sm:block">
              {step.detail}
            </div>
          </div>
          {/* Arrow */}
          {i < PIPELINE_STEPS.length - 1 && (
            <div className="text-gray-300 text-lg font-light flex-shrink-0 self-start mt-3 sm:mt-2 rotate-90 sm:rotate-0 mx-auto sm:mx-0">
              →
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

// ─── Summary stats ────────────────────────────────────────────────────────────
const SummaryStats = () => {
  const active  = PROVIDERS.filter((p) => p.status === "active").length;
  const pending = PROVIDERS.filter((p) => p.status === "pending").length;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {[
        { label: "Total Providers",   value: PROVIDERS.length, icon: FiGlobe,        color: "text-blue-600",  bg: "bg-blue-50"  },
        { label: "Active",            value: active,           icon: FiCheckCircle,  color: "text-green-600", bg: "bg-green-50" },
        { label: "Pending",           value: pending,          icon: FiClock,        color: "text-yellow-600",bg: "bg-yellow-50"},
        { label: "DB Tables Proposed",value: "2",              icon: FiDatabase,     color: "text-purple-600",bg: "bg-purple-50"},
      ].map((s) => (
        <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}>
            <SafeIcon icon={s.icon} className={`${s.color} text-base`} />
          </div>
          <div>
            <div className="text-xl font-bold text-gray-900">{s.value}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main export ──────────────────────────────────────────────────────────────
const DeviceIntegrationHub = () => {
  const [filter, setFilter] = useState("all");

  const filtered = PROVIDERS.filter((p) => {
    if (filter === "active")  return p.status === "active";
    if (filter === "pending") return p.status === "pending";
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Device-Agnostic Integration Hub
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Provider connection status and telemetry gateway architecture.
            Fitbit, Oura, and Bluetooth are marked Pending until backend support is confirmed.
          </p>
        </div>
        {/* Filter toggle */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1 self-start sm:self-auto flex-shrink-0">
          {["all", "active", "pending"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md capitalize transition-all ${
                filter === f
                  ? "bg-white shadow-sm text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      <SummaryStats />

      {/* Provider cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence>
          {filtered.map((p) => (
            <motion.div
              key={p.id}
              layout
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.2 }}
            >
              <ProviderCard provider={p} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Gateway pipeline */}
      <GatewayPipeline />

      {/* DB schema status */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <SafeIcon icon={FiDatabase} className="text-purple-600" />
          <h3 className="font-semibold text-gray-900">Database Schema Status</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            {
              table: "tenants",
              note: "Multi-tenant anchor table",
              status: "Proposed v2",
              cls: "bg-yellow-50 border-yellow-200 text-yellow-800",
            },
            {
              table: "devices",
              note: "Device registration per patient",
              status: "Proposed v2",
              cls: "bg-yellow-50 border-yellow-200 text-yellow-800",
            },
            {
              table: "telemetry_events",
              note: "Append-only vitals log",
              status: "Proposed v2",
              cls: "bg-yellow-50 border-yellow-200 text-yellow-800",
            },
          ].map((t) => (
            <div
              key={t.table}
              className={`rounded-lg border p-3 flex items-center justify-between gap-3 ${t.cls}`}
            >
              <div>
                <code className="text-xs font-mono font-semibold">{t.table}</code>
                <div className="text-[11px] mt-0.5 opacity-80">{t.note}</div>
              </div>
              <span className="text-[11px] font-semibold whitespace-nowrap">{t.status}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-3 flex items-center gap-1.5">
          <SafeIcon icon={FiShield} className="text-gray-400" />
          Schema proposal v2 submitted for manager approval. Migration will run in staging first.
        </p>
      </div>
    </div>
  );
};

export default DeviceIntegrationHub;